import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Labeled, Stack, TextInputField } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IFileUploaderValue } from '~/components/organisms/fields/FileUploader';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import { FormField } from '~/components/atoms/FormField';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { FormFileUploader } from '~/components/organisms/fields/FileUploader';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { useApi } from '~/lib/api';

export type IUserProfileFormValues = {
  firstName?: string;
  lastName?: string;
  username?: string;
  emailAddress?: string;
  pictures?: Array<IFileUploaderValue>;
};

export type IUserProfileFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IUserProfileFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    mode: IFormMode;
    defaultValues?: DefaultValues<IUserProfileFormValues>;
    onSubmit?: SubmitHandler<IUserProfileFormValues>;
  };

export const UserProfileForm: React.FC<IUserProfileFormProps> = (props) => {
  const { sx, defaultValues, onSubmit, ...other } = props;
  const { t } = useTranslation();
  const form = usePersistentForm<IUserProfileFormValues>({ defaultValues });
  const api = useApi();
  const appRegion = api.getAuthorization()?.region;

  return (
    <ContentBlock.FormBody sx={sx} form={form} onSubmit={onSubmit} {...other}>
      <Labeled label={t('common.firstName')} required>
        <FormField
          name="firstName"
          control={form.control}
          as={TextInputField}
          forwardError
          rules={{ minLength: 2 }}
        />
      </Labeled>
      <Labeled label={t('common.lastName')} required>
        <FormField
          name="lastName"
          control={form.control}
          as={TextInputField}
          forwardError
          rules={{ minLength: 2 }}
        />
      </Labeled>
      <Labeled label={t('UserProfileForm.field.username.label')} required>
        <FormField
          name="username"
          control={form.control}
          as={TextInputField}
          forwardError
          rules={{ minLength: 2 }}
        />
      </Labeled>
      <Labeled label={t('common.emailAddress')} required>
        <FormField
          name="emailAddress"
          control={form.control}
          as={TextInputField}
          forwardError
          type="email"
        />
      </Labeled>
      <Labeled label={t('UserProfileForm.profile-picture')}>
        <FormFileUploader
          name="pictures"
          control={form.control}
          maxFiles={1}
          acceptTypes={['picture']}
        />
      </Labeled>
      <Labeled label="Region">
        <Stack horizontal gap={2}>
          <FontAwesomeIcon icon={faGlobe} />
          {appRegion}
        </Stack>
      </Labeled>
    </ContentBlock.FormBody>
  );
};
